import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  PermissionListData,
  Roles,
  RoleUpdateRequest,
  UpdateRoleCapabilitiesRequest,
  UpdateRoleCapabilitiesResponse,
  RoleCreateRequest,
} from './model';
import { ApiResponse, ListResponse } from '../../../model';
import { API_CONSTANTS } from '../../../utils/constants';
import { baseQueryWithReauth } from '../../../apis/baseQuery';

export const roleApi = createApi({
  reducerPath: 'roleApi',
  tagTypes: ['RoleList', 'PermissionList'],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getRoleList: builder.query<ListResponse<Roles>, void>({
      query: (body) => ({
        url: API_CONSTANTS.ROLE.ROLE_URL,
        method: 'POST',
        body,
      }),
      providesTags: ['RoleList'],
    }),
    updateRole: builder.mutation<ApiResponse<object>, RoleUpdateRequest>({
      query: ({ id, ...body }) => ({
        url: `${API_CONSTANTS.ROLE.EDIT_ROLE_URL}${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['RoleList'],
    }),
    createRole: builder.mutation<ApiResponse<object>, RoleCreateRequest>({
      query: (body) => ({
        url: API_CONSTANTS.ROLE.ADD_ROLE_URL,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['RoleList'],
    }),
    getPermissionList: builder.query<ApiResponse<PermissionListData>, void>({
      query: () => ({
        url: API_CONSTANTS.ROLE.PERMISSION_URL,
        method: 'GET',
      }),
      providesTags: ['PermissionList'],
    }),
    getRoleById: builder.query<ApiResponse<Roles>, number>({
      query: (id) => ({
        url: `${API_CONSTANTS.ROLE.EDIT_ROLE_URL}${id}`,
        method: 'GET',
      }),
    }),
    updateRoleCapabilities: builder.mutation<
      ApiResponse<UpdateRoleCapabilitiesResponse>,
      UpdateRoleCapabilitiesRequest
    >({
      query: ({ groupId, groupCode, groupName, capabilities }) => ({
        url: `${API_CONSTANTS.ROLE.GROUP_CAPABILITY_UPDATE_URL}${groupId}`,
        method: 'PUT',
        body: {
          groupCode,
          groupName,
          capabilities,
        },
      }),
      invalidatesTags: ['RoleList'],
    }),
  }),
});

export const {
  useGetRoleListQuery,
  useLazyGetRoleListQuery,
  useUpdateRoleMutation,
  useLazyGetPermissionListQuery,
  useLazyGetRoleByIdQuery,
  useUpdateRoleCapabilitiesMutation,
  useCreateRoleMutation,
} = roleApi;

export default roleApi;
